import React from "react";
import { Link } from "react-router-dom";
import { BookOpen, FlaskConical, Microscope, ShieldAlert, ArrowUpRight } from "lucide-react";
import { LAB_ABSTRACT } from "../data/products";
import { usePageMeta } from "../hooks/usePageMeta";

const PLACEHOLDER_CARDS = [
    {
        id: "bpc-157-research",
        icon: FlaskConical,
        category: "Peptide Research",
        title: "BPC-157 Research: What Preclinical Studies Have Investigated",
        excerpt:
            "An educational overview of the preclinical BPC-157 literature — gastrointestinal, tendon and ligament, angiogenesis and VEGF studies, proposed mechanisms, and the limits of current evidence.",
        href: "/research/bpc-157-research",
        readLabel: "12 min read",
    },
    {
        id: "receptor-signaling",
        icon: Microscope,
        category: "Molecular Biology",
        title: "Receptor Signaling in Research Models",
        excerpt:
            "A curated overview of receptor-ligand interactions studied across published in-vitro assays involving research-grade peptides.",
        href: "/research/receptor-signaling",
        readLabel: "14 min read",
    },
    {
        id: "handling-protocols",
        icon: BookOpen,
        category: "Lab Protocols",
        title: "Handling & Reconstitution of Lyophilized Research Peptides",
        excerpt:
            "General laboratory concepts on lyophilized peptide handling, reconstitution, storage, stability, and contamination avoidance. Not a compound-specific protocol.",
        href: "/research/handling-reconstitution",
        readLabel: "6 min read",
    },
];

export const Research = () => {
    usePageMeta({
        title: "Research Library — Quantum Genesis Labs",
        description:
            "A curated research library of peptide chemistry fundamentals, molecular biology overviews, and laboratory handling protocols. For Research Use Only.",
        path: "/research",
    });

    return (
        <div className="relative" data-testid="research-page">
            {/* Ambient lab imagery */}
            <div className="absolute inset-x-0 top-0 h-[440px] overflow-hidden -z-10">
                <img src={LAB_ABSTRACT} alt="" className="w-full h-full object-cover opacity-20" />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#05050A]" />
            </div>

            <div className="max-w-6xl mx-auto px-5 lg:px-10 py-20">
                {/* Hero */}
                <header className="text-center max-w-3xl mx-auto mb-14">
                    <p className="text-xs tracking-[0.25em] uppercase text-indigo-400 mb-3">Knowledge Base</p>
                    <h1 className="text-4xl md:text-5xl font-light tracking-tighter">
                        Research <span className="text-gradient">library</span>
                    </h1>
                    <p className="mt-5 text-slate-400 leading-relaxed">
                        Curated fundamentals, molecular biology overviews, and laboratory
                        handling protocols — written for research professionals.
                    </p>
                </header>

                {/* Research Use Only disclaimer callout */}
                <div
                    data-testid="research-ruo-disclaimer"
                    className="mx-auto max-w-3xl mb-16 rounded-2xl border border-indigo-500/25 bg-gradient-to-br from-indigo-500/[0.06] to-violet-500/[0.04] px-5 sm:px-7 py-5 sm:py-6"
                >
                    <div className="flex items-start gap-3.5">
                        <div className="w-9 h-9 rounded-lg bg-indigo-500/15 border border-indigo-500/25 flex items-center justify-center shrink-0">
                            <ShieldAlert className="w-4 h-4 text-indigo-300" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-[10px] tracking-[0.28em] uppercase text-indigo-300 mb-1.5">
                                Research Use Only
                            </p>
                            <p className="text-[13.5px] sm:text-sm text-slate-300 leading-[1.7]">
                                All content in this library is provided for scientific and informational
                                purposes only. Products referenced are not intended for human consumption,
                                veterinary use, diagnosis, treatment, or prevention of disease.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Placeholder cards */}
                <section aria-label="Research articles">
                    <div className="flex items-baseline justify-between mb-6">
                        <h2 className="text-[11px] tracking-[0.28em] uppercase text-slate-500">
                            Featured articles
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="research-cards">
                        {PLACEHOLDER_CARDS.map((c) => {
                            const Icon = c.icon;
                            const isPublished = Boolean(c.href);
                            const cardInner = (
                                <>
                                    <div
                                        className="h-32 bg-gradient-to-br from-indigo-500/[0.14] to-violet-500/[0.08] border-b border-white/5 flex items-center justify-center relative"
                                    >
                                        <div className="w-12 h-12 rounded-xl bg-[#0A0A12]/80 border border-white/10 flex items-center justify-center shadow-[0_0_28px_-4px_rgba(99,102,241,0.35)]">
                                            <Icon className="w-5 h-5 text-indigo-300" strokeWidth={1.8} />
                                        </div>
                                    </div>

                                    <div className="flex-1 p-5 sm:p-6 flex flex-col">
                                        <p className="text-[10px] tracking-[0.22em] uppercase text-indigo-400 mb-2">
                                            {c.category}
                                        </p>
                                        <h3 className="text-lg font-medium text-white leading-snug tracking-tight">
                                            {c.title}
                                        </h3>
                                        <p className="mt-3 text-sm text-slate-400 leading-relaxed line-clamp-3">
                                            {c.excerpt}
                                        </p>

                                        <div className="mt-6 pt-4 border-t border-white/5 flex items-center justify-between">
                                            <span className="inline-flex items-center gap-1.5 text-[11px] tracking-wide text-slate-500">
                                                <span className={`w-1.5 h-1.5 rounded-full ${isPublished ? "bg-emerald-400/80" : "bg-indigo-400/60"}`} />
                                                {isPublished ? c.readLabel : "Coming soon"}
                                            </span>
                                            <span
                                                aria-disabled={!isPublished}
                                                className={`inline-flex items-center gap-1 text-[12.5px] ${isPublished ? "text-indigo-300 group-hover:text-indigo-200" : "text-slate-500"}`}
                                            >
                                                Read <ArrowUpRight className="w-3.5 h-3.5" />
                                            </span>
                                        </div>
                                    </div>
                                </>
                            );

                            const className = "group relative flex flex-col rounded-2xl border border-white/10 bg-[#0A0A12]/70 overflow-hidden transition-colors hover:border-indigo-400/30";

                            return isPublished ? (
                                <Link
                                    key={c.id}
                                    to={c.href}
                                    data-testid={`research-card-${c.id}`}
                                    className={className}
                                >
                                    {cardInner}
                                </Link>
                            ) : (
                                <article
                                    key={c.id}
                                    data-testid={`research-card-${c.id}`}
                                    className={className}
                                >
                                    {cardInner}
                                </article>
                            );
                        })}
                    </div>
                </section>

                {/* Footer CTA back to catalog */}
                <div className="mt-16 text-center">
                    <p className="text-sm text-slate-500 leading-relaxed max-w-xl mx-auto">
                        Looking for research-grade peptides for your next study?
                    </p>
                    <Link
                        to="/products"
                        data-testid="research-to-catalog"
                        className="mt-4 inline-flex items-center gap-2 h-11 px-6 rounded-full bg-indigo-500 hover:bg-indigo-400 text-white text-sm font-medium tracking-tight transition-colors shadow-[0_0_24px_-6px_rgba(99,102,241,0.7)]"
                    >
                        Browse the catalog
                        <ArrowUpRight className="w-4 h-4" />
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default Research;
